import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchPathPreviewState } from "@/api/fetchPathPreviewState";
import type {
  PreviewCompletionEntry,
  PreviewCompletionsMap,
  PreviewPersonalization,
} from "@/api/fetchPathPreviewState";
import type { ViewerState } from "../types/viewerState";

/**
 * Admin path-preview state. Drives the SAME viewer-state payload as the learner
 * viewer, but through /preview-state with a client-owned simulated-completion
 * map — nothing is persisted server-side.
 *
 * The completion map is mutated SYNCHRONOUSLY by reducers (never via the async
 * fulfilled), so rapid successive actions accumulate correctly instead of each
 * rebuilding from a stale pre-network snapshot. recomputePreview then re-derives
 * the viewer state from the current map; a monotonic seq + pathId guard drops
 * out-of-order / cross-path stale responses.
 */
export interface PathPreviewSliceState {
  pathId: number | null;
  viewerState: ViewerState | null;
  loading: boolean;
  error: string | null;
  /** Simulated completions keyed by item id — the source of every recompute. */
  completions: PreviewCompletionsMap;
  personalization: PreviewPersonalization | null;
  /** Days pretended to have elapsed since enrollment (previews time gates). */
  simulatedDaysElapsed: number;
  /** Monotonic recompute counter; guards viewerState against out-of-order responses. */
  seq: number;
  appliedSeq: number;
  /** In-flight recomputes (drives the preview bar's busy state). */
  pendingRecomputes: number;
  /**
   * requestId of the newest loadPreview. Only that load may paint: StrictMode
   * mounts the viewer twice, and a superseded load landing after the first
   * click used to wipe the simulated map mid-session.
   */
  loadRequestId: string | null;
}

const initialState: PathPreviewSliceState = {
  pathId: null,
  viewerState: null,
  loading: false,
  error: null,
  pendingRecomputes: 0,
  completions: {},
  personalization: null,
  simulatedDaysElapsed: 0,
  seq: 0,
  appliedSeq: 0,
  loadRequestId: null,
};

interface ThunkStateSlice {
  pathPreview: PathPreviewSliceState;
}

const extractApiError = (err: unknown, fallback: string): string => {
  const axiosError = err as { response?: { data?: { error?: string } } };
  if (axiosError?.response?.data?.error) {
    return axiosError.response.data.error;
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return fallback;
};

/** Initial load: resets the simulated state and fetches the fresh state. */
export const loadPreview = createAsyncThunk(
  "pathPreview/load",
  async (pathId: number, { rejectWithValue }) => {
    try {
      const viewerState = await fetchPathPreviewState(pathId, {}, null, 0);
      return { pathId, viewerState };
    } catch (err) {
      return rejectWithValue(extractApiError(err, "Failed to load preview"));
    }
  },
);

/**
 * Re-derive the viewer state from the CURRENT simulated map (read fresh from the
 * store, not a caller snapshot). Callers mutate the map via the synchronous
 * reducers first, then dispatch this. Rejects on failure so the shared body's
 * handler wrappers surface the error banner.
 */
export const recomputePreview = createAsyncThunk(
  "pathPreview/recompute",
  async (_arg: void, { getState, rejectWithValue }) => {
    const s = (getState() as ThunkStateSlice).pathPreview;
    if (s.pathId === null) {
      return rejectWithValue("Preview is not loaded");
    }
    // seq was just incremented by recomputePreview.pending.
    const { pathId, seq, completions, personalization, simulatedDaysElapsed } =
      s;
    try {
      const viewerState = await fetchPathPreviewState(
        pathId,
        completions,
        personalization,
        simulatedDaysElapsed,
      );
      return { pathId, seq, viewerState };
    } catch (err) {
      return rejectWithValue(extractApiError(err, "Failed to update preview"));
    }
  },
);

const pathPreviewSlice = createSlice({
  name: "pathPreview",
  initialState,
  reducers: {
    clearPreview: () => initialState,
    /** Full-map replace (Complete All / Reset / Complete Up To). */
    previewCompletionsReplaced: (
      state,
      action: PayloadAction<PreviewCompletionsMap>,
    ) => {
      state.completions = action.payload;
    },
    previewItemCompleted: (
      state,
      action: PayloadAction<{ itemId: number; entry?: PreviewCompletionEntry }>,
    ) => {
      state.completions[action.payload.itemId] = action.payload.entry ?? {};
    },
    previewItemCleared: (state, action: PayloadAction<number>) => {
      delete state.completions[action.payload];
    },
    /** Learner box-tick is add-only (a checkbox can't be unchecked). */
    previewChecklistTicked: (
      state,
      action: PayloadAction<{ itemId: number; index: number }>,
    ) => {
      const { itemId, index } = action.payload;
      const entry = state.completions[itemId] ?? {};
      const indices = new Set(entry.checklistIndices ?? []);
      indices.add(index);
      state.completions[itemId] = {
        ...entry,
        checklistIndices: [...indices].sort((a, b) => a - b),
      };
    },
    previewDaysElapsedSet: (state, action: PayloadAction<number>) => {
      state.simulatedDaysElapsed = Math.max(0, action.payload);
    },
    /** Audience simulation (role/trade edge pruning). Null = general audience. */
    previewPersonalizationSet: (
      state,
      action: PayloadAction<PreviewPersonalization | null>,
    ) => {
      state.personalization = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // A (re)load starts a fresh simulation. Reset the map here, when the
      // load is issued, never when it lands: by then the user may have acted.
      .addCase(loadPreview.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.loadRequestId = action.meta.requestId;
        state.completions = {};
        state.personalization = null;
        state.simulatedDaysElapsed = 0;
        state.seq = 0;
        state.appliedSeq = 0;
      })
      .addCase(loadPreview.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.loadRequestId) {
          return; // superseded load
        }
        state.loading = false;
        state.pathId = action.payload.pathId;
        // A recompute issued since the load started carries newer state.
        if (state.seq === 0) {
          state.viewerState = action.payload.viewerState;
        }
      })
      .addCase(loadPreview.rejected, (state, action) => {
        if (action.meta.requestId !== state.loadRequestId) {
          return;
        }
        state.loading = false;
        state.error = (action.payload as string) || "Failed to load preview";
      })
      // recompute never toggles `loading` (that blanks the screen); the shared
      // body owns per-action pending spinners.
      .addCase(recomputePreview.pending, (state) => {
        state.seq += 1;
        state.pendingRecomputes += 1;
      })
      .addCase(recomputePreview.fulfilled, (state, action) => {
        state.pendingRecomputes = Math.max(0, state.pendingRecomputes - 1);
        // Drop cross-path and out-of-order stale responses.
        if (
          action.payload.pathId === state.pathId &&
          action.payload.seq >= state.appliedSeq
        ) {
          state.viewerState = action.payload.viewerState;
          state.appliedSeq = action.payload.seq;
        }
      })
      .addCase(recomputePreview.rejected, (state) => {
        state.pendingRecomputes = Math.max(0, state.pendingRecomputes - 1);
      });
  },
});

export const {
  clearPreview,
  previewCompletionsReplaced,
  previewItemCompleted,
  previewItemCleared,
  previewChecklistTicked,
  previewDaysElapsedSet,
  previewPersonalizationSet,
} = pathPreviewSlice.actions;
export default pathPreviewSlice.reducer;
